import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { AiPermissionsSection, SupportingDocumentsSection, isSectionEnabled, useAppSections } from "@lumanity/ui/projects";
import { Collapsible, CollapsibleContent, CollapsibleTrigger, toast } from "@lumanity/ui/primitives";
import TeamRoles from "@/features/team/TeamRoles";
import { cn } from "@/lib/utils";

interface ProjectDetailsPanelProps {
  projectId: string;
  /** Owners and editors may change guidance documents. */
  canEditSetup: boolean;
}

/**
 * The project's team (Writer / Senior Reviewer roles), guidance and context
 * documents, and confirmed AI permissions — collapsed under the project header
 * so it's reachable from every step without crowding the flow.
 */
const ProjectDetailsPanel = ({ projectId, canEditSetup }: Readonly<ProjectDetailsPanelProps>) => {
  const [open, setOpen] = useState(false);
  const { data: sections } = useAppSections();

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border bg-card">
      <CollapsibleTrigger className="flex w-full items-center justify-between px-5 py-3 text-sm font-semibold text-brand-navy">
        Project details
        <span className="flex items-center gap-2 font-normal text-muted-foreground">
          Team, guidance documents, AI permissions
          <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-6 border-t px-5 py-5">
        <section>
          <h3 className="text-base font-semibold text-brand-navy">Team</h3>
          <p className="mb-3 text-sm text-muted-foreground">
            Writers draft and inspect outputs; Senior Reviewers run independent QC. One person can hold both, but never QCs an output they wrote.
          </p>
          <TeamRoles projectId={projectId} />
        </section>

        {isSectionEnabled(sections, "supporting_documents") && (
          <SupportingDocumentsSection
            projectId={projectId}
            readOnly={!canEditSetup}
            labels={{
              title: "Guidance & context documents",
              description: "SOWs, style guides, lexicons and examples. They guide language and structure but are never cited as scientific evidence.",
            }}
            onError={(e) => toast({ title: "Supporting document", description: e.message, variant: "destructive" })}
          />
        )}

        {isSectionEnabled(sections, "ai_permissions") && <AiPermissionsSection projectId={projectId} />}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ProjectDetailsPanel;
